/**
 * Settings the platform admin can change without a release. Each key holds only the changes from
 * the code defaults, so leaving a key empty keeps today's behaviour. Bounds keep every value safe:
 * nothing here can lock everyone out.
 */
import { z } from "zod";
import { platformPlansSchema } from "../entitlements";
import { platformPacksSchema } from "../packs/overrides";
import { PLAN_IDS, type PlanId } from "../plans";

export const SECURITY_DEFAULTS = {
  otpTtlMinutes: 5,
  otpMaxAttempts: 5,
  otpResendSeconds: 60,
  otpMaxPer15Min: 3,
  otpMaxPerDay: 10,
  passwordMaxFailures: 5,
  passwordWindowMinutes: 15,
  sessionIdleHours: 12,
  sessionTrustedIdleDays: 30,
  sessionAbsoluteDays: 90,
  adminIdleMinutes: 30,
  adminMaxHours: 8,
} as const;

/** Allowed range for each security setting: [min, max]. */
export const SECURITY_BOUNDS: Record<keyof typeof SECURITY_DEFAULTS, readonly [number, number]> = {
  otpTtlMinutes: [2, 15],
  otpMaxAttempts: [3, 10],
  otpResendSeconds: [30, 300],
  otpMaxPer15Min: [2, 10],
  otpMaxPerDay: [5, 50],
  passwordMaxFailures: [3, 20],
  passwordWindowMinutes: [5, 120],
  sessionIdleHours: [1, 72],
  sessionTrustedIdleDays: [1, 90],
  sessionAbsoluteDays: [7, 365],
  adminIdleMinutes: [10, 120],
  adminMaxHours: [1, 24],
};

export const securitySettingsSchema = z
  .object(
    Object.fromEntries(
      Object.entries(SECURITY_BOUNDS).map(([k, [min, max]]) => [
        k,
        z.number().int().min(min).max(max),
      ]),
    ) as Record<keyof typeof SECURITY_DEFAULTS, z.ZodNumber>,
  )
  .partial()
  .strict();
export type SecuritySettings = { [K in keyof typeof SECURITY_DEFAULTS]: number };

export const SIGNUP_DEFAULTS = {
  allowNewAccounts: true,
  allowNewBusinesses: true,
  allowPasswordSignIn: true,
  allowGoogleSignIn: true,
  /** Businesses one person may own (not closed), paid or free. */
  maxBusinessesPerUser: 5,
  /** Of those, how many may be on the Free plan: another shop on Free should be a branch instead. */
  maxFreeBusinessesPerUser: 1,
};
export const BUSINESS_LIMIT_BOUNDS = {
  maxBusinessesPerUser: [1, 100],
  maxFreeBusinessesPerUser: [0, 20],
} as const;
export const signupSettingsSchema = z
  .object({
    allowNewAccounts: z.boolean(),
    allowNewBusinesses: z.boolean(),
    allowPasswordSignIn: z.boolean(),
    allowGoogleSignIn: z.boolean(),
    maxBusinessesPerUser: z.number().int().min(1).max(100),
    maxFreeBusinessesPerUser: z.number().int().min(0).max(20),
  })
  .partial()
  .strict();
export type SignupSettings = typeof SIGNUP_DEFAULTS;

const message = z
  .object({ en: z.string().trim().max(300), bn: z.string().trim().max(300) })
  .strict();
export const maintenanceSettingsSchema = z
  .object({
    /** Every business becomes read-only; staff on support visits are exempt. */
    enabled: z.boolean(),
    message,
    /** Businesses made read-only one by one. */
    readOnlyTenants: z.array(z.string().min(1).max(64)).max(1000),
  })
  .partial()
  .strict();
export type MaintenanceSettings = z.infer<typeof maintenanceSettingsSchema>;

/** AI requests each plan may make per month; 0 turns AI off for that plan. */
export const AI_DEFAULTS = {
  enabled: true,
  monthlyQuota: { free: 20, starter: 200, business: 1000, enterprise: 5000 } as Record<
    PlanId,
    number
  >,
  /** The weekly summary emailed to owners. */
  weeklySummary: true,
};
export const aiSettingsSchema = z
  .object({
    enabled: z.boolean(),
    monthlyQuota: z.partialRecord(z.enum(PLAN_IDS), z.number().int().min(0).max(100_000)),
    weeklySummary: z.boolean(),
  })
  .partial()
  .strict();
export type AiSettings = typeof AI_DEFAULTS;

export const PLATFORM_SETTING_SCHEMAS = {
  plans: platformPlansSchema,
  packs: platformPacksSchema,
  security: securitySettingsSchema,
  signup: signupSettingsSchema,
  maintenance: maintenanceSettingsSchema,
  ai: aiSettingsSchema,
} as const;
export type PlatformSettingKey = keyof typeof PLATFORM_SETTING_SCHEMAS;
export const PLATFORM_SETTING_KEYS = Object.keys(PLATFORM_SETTING_SCHEMAS) as PlatformSettingKey[];

export type PlatformConfig = {
  [K in PlatformSettingKey]: z.infer<(typeof PLATFORM_SETTING_SCHEMAS)[K]>;
};

export const EMPTY_PLATFORM_CONFIG: PlatformConfig = {
  plans: {},
  packs: {},
  security: {},
  signup: {},
  maintenance: {},
  ai: {},
};

export function securityOf(config: Pick<PlatformConfig, "security">): SecuritySettings {
  return { ...SECURITY_DEFAULTS, ...config.security };
}

export function aiOf(config: Pick<PlatformConfig, "ai">): AiSettings {
  return {
    ...AI_DEFAULTS,
    ...config.ai,
    monthlyQuota: { ...AI_DEFAULTS.monthlyQuota, ...config.ai.monthlyQuota },
  };
}

export function signupOf(config: Pick<PlatformConfig, "signup">): SignupSettings {
  return { ...SIGNUP_DEFAULTS, ...config.signup };
}

/** Whether a business may only be read right now, and the message to show. */
export function maintenanceFor(config: Pick<PlatformConfig, "maintenance">, tenantId: string) {
  const m = config.maintenance;
  const readOnly = m.enabled === true || (m.readOnlyTenants ?? []).includes(tenantId);
  return readOnly
    ? { readOnly: true as const, message: m.message ?? null, global: m.enabled === true }
    : null;
}

const announcementText = (max: number) => z.string().trim().max(max);
export const announcementSchema = z
  .object({
    titleEn: announcementText(120).min(1),
    titleBn: announcementText(120).min(1),
    bodyEn: announcementText(1000).default(""),
    bodyBn: announcementText(1000).default(""),
    severity: z.enum(["info", "warning", "critical"]).default("info"),
    audience: z
      .object({
        kind: z.enum(["all", "plans", "packs", "tenants"]),
        values: z.array(z.string().min(1).max(64)).max(500).default([]),
      })
      .refine((a) => a.kind === "all" || a.values.length > 0, "Choose who sees it"),
    startsAt: z.number().int().positive(),
    endsAt: z.number().int().positive().nullable().default(null),
    dismissible: z.boolean().default(true),
  })
  .refine((v) => v.endsAt === null || v.endsAt > v.startsAt, {
    message: "Ends before it starts",
    path: ["endsAt"],
  });
export type AnnouncementInput = z.output<typeof announcementSchema>;
